/**
 * Recurring Expense Fields Component
 * 
 * Reusable frequency and end date inputs for recurring expenses.
 */

import { Info, AlertCircle } from 'lucide-react';
import type { RecurringFrequency } from '@/lib/domains';

interface RecurringExpenseFieldsProps {
    frequency: RecurringFrequency;
    endDate: string;
    minEndDate: string;
    onFrequencyChange: (frequency: RecurringFrequency) => void;
    onEndDateChange: (endDate: string) => void;
    showFrequencyWarning?: boolean;
    endDateError?: string;
}

export function RecurringExpenseFields({
    frequency,
    endDate,
    minEndDate,
    onFrequencyChange,
    onEndDateChange,
    showFrequencyWarning = false,
    endDateError
}: RecurringExpenseFieldsProps) {
    return (
        <>
            {/* Frequency */}
            <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Frequency *
                </label>
                <select
                    value={frequency}
                    onChange={(e) => onFrequencyChange(e.target.value as RecurringFrequency)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none bg-white"
                >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                </select>
                {showFrequencyWarning && (
                    <p className="text-sm text-blue-600 mt-2 flex items-center gap-1">
                        <Info className="w-4 h-4" />
                        Next due date will be recalculated based on new frequency
                    </p>
                )}
            </div>

            {/* End Date */}
            <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date (optional)
                </label>
                <input
                    type="date"
                    value={endDate}
                    onChange={(e) => onEndDateChange(e.target.value)}
                    min={minEndDate}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none"
                />
                {endDateError && (
                    <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {endDateError}
                    </p>
                )}
                {!endDateError && (
                    <p className="text-xs text-gray-500 mt-1">
                        Leave empty to continue indefinitely
                    </p>
                )}
            </div>
        </>
    );
}
